import React from 'react';
import { Alert, Card, Progress, Skeleton, Space, Typography, theme } from 'antd';
import {
  CheckCircleTwoTone,
  ExclamationCircleTwoTone,
  CloseCircleTwoTone,
  QuestionCircleTwoTone,
} from '@ant-design/icons';
import ComplianceBadge from './ComplianceBadge';
import useTenantFlags from 'hooks/useTenantFlags';

const { Text } = Typography;

const STATUS_ICON = {
  verde: <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 28 }} />,
  amarillo: <ExclamationCircleTwoTone twoToneColor="#faad14" style={{ fontSize: 28 }} />,
  rojo: <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ fontSize: 28 }} />,
  sin_datos: <QuestionCircleTwoTone twoToneColor="#bfbfbf" style={{ fontSize: 28 }} />,
};

const STATUS_TITLE = {
  verde: 'Al día con la normativa',
  amarillo: 'Atención — hay observaciones regulatorias',
  rojo: 'Cumplimiento crítico — acción requerida',
  sin_datos: 'No hay datos para computar cumplimiento',
};

const STATUS_SUBTITLE = {
  verde: 'Todas las certificaciones vigentes y datos MINVU completos.',
  amarillo: 'Hay items que requieren coordinación con el certificador o mantenedor.',
  rojo: 'Hay al menos una certificación vencida o mantención crítica sin ejecutar.',
  sin_datos: 'Registra edificios y equipos para habilitar el semáforo.',
};

const STROKE_COLOR = {
  verde: '#52c41a',
  amarillo: '#faad14',
  rojo: '#ff4d4f',
  sin_datos: '#bfbfbf',
};

function renderList(title, items, type) {
  if (!items || items.length === 0) return null;
  const visible = items.slice(0, 5);
  const rest = items.length - visible.length;
  return (
    <Alert
      type={type}
      showIcon
      style={{ marginTop: 8 }}
      message={`${title} (${items.length})`}
      description={
        <Space direction="vertical" size={2}>
          {visible.map((msg, i) => (
            <Text key={`${title}-${i}`}>{msg}</Text>
          ))}
          {rest > 0 && <Text type="secondary">…y {rest} más</Text>}
        </Space>
      }
    />
  );
}

// Panel de cumplimiento consolidado. Se coloca al tope del tab General de
// la ficha de cliente. Muestra status + score + breakdown accionable.
function CompliancePanel({ data, loading, error, title = 'Cumplimiento regulatorio MINVU' }) {
  const { minvuEnabled } = useTenantFlags();
  const { token } = theme.useToken();

  if (!minvuEnabled) return null;

  if (loading && !data) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 2 }} />
      </Card>
    );
  }
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudo cargar el cumplimiento"
        description={error?.message ?? 'Reintentá refrescando la página.'}
      />
    );
  }
  if (!data) return null;

  const status = data.status ?? 'sin_datos';
  const score = typeof data.score === 'number' ? data.score : null;
  const totalEquipos = data.context?.totalEquipos ?? 0;
  const totalEdificios = data.context?.totalEdificios;

  return (
    <Card variant="borderless" style={{ boxShadow: token.boxShadowSecondary }}>
      <Space align="start" size="large" style={{ width: '100%' }} wrap>
        <div style={{ minWidth: 48 }}>{STATUS_ICON[status]}</div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <Space wrap>
            <Text strong style={{ fontSize: 16 }}>
              {title}
            </Text>
            <ComplianceBadge status={status} score={score ?? undefined} />
          </Space>
          <div style={{ marginTop: 4 }}>
            <Text>{STATUS_TITLE[status]}</Text>
          </div>
          <div>
            <Text type="secondary">{STATUS_SUBTITLE[status]}</Text>
          </div>
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {totalEdificios != null ? `${totalEdificios} edificios · ` : ''}
              {totalEquipos} equipos
            </Text>
          </div>
        </div>
        {score != null && (
          <Progress type="circle" percent={score} size={80} strokeColor={STROKE_COLOR[status]} />
        )}
      </Space>

      {renderList('Críticos', data.criticals, 'error')}
      {renderList('Atención', data.warnings, 'warning')}
      {renderList('Información', data.info, 'info')}
    </Card>
  );
}

export default CompliancePanel;
